import { useDebounce } from "hooks/use-debounce";
import { useGetUsers } from "features/idp/iam/hooks/use-user";
import { User as UserType } from "features/idp/iam/models/user";
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { USER_PAGE_SIZE } from "../utils/filter-types";

type UseUserSearchOptions = {
  projectKey: string;
  currentFilterType: string;
  queryParams: {
    user_id: string;
    user_email: string;
    filter_type: string;
  };
  setQueryParams: Dispatch<SetStateAction<any>>;
};

export const useUserSearch = ({
  projectKey,
  currentFilterType,
  queryParams,
  setQueryParams,
}: UseUserSearchOptions) => {
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [debouncedUserSearch, setDebouncedUserSearch] = useState("");
  const [userCurrentPage, setUserCurrentPage] = useState(0);
  const [allUsers, setAllUsers] = useState<UserType[]>([]);
  const [hasMoreUsers, setHasMoreUsers] = useState(true);
  const [isUserPopoverOpen, setIsUserPopoverOpen] = useState(false);
  const commandListRef = useRef<HTMLDivElement>(null);

  const debouncedUserSearchValue = useDebounce(userSearchTerm, 500);

  useEffect(() => {
    setDebouncedUserSearch(debouncedUserSearchValue);
  }, [debouncedUserSearchValue]);

  useEffect(() => {
    setUserCurrentPage(0);
    setAllUsers([]);
    setHasMoreUsers(true);
  }, [debouncedUserSearch]);

  const userFilter = {
    email: currentFilterType === "email" ? debouncedUserSearch : "",
    name: currentFilterType === "name" ? debouncedUserSearch : "",
  };

  const {
    data: usersData,
    isLoading: isUsersLoading,
    isFetching: isUsersFetching,
  } = useGetUsers({
    page: userCurrentPage,
    pageSize: USER_PAGE_SIZE,
    filter: userFilter,
    enabled: true,
  });

  useEffect(() => {
    if (!usersData?.data) return;

    const items = usersData.data;
    const totalCount = usersData.totalCount || 0;

    if (totalCount === 0) {
      setAllUsers([]);
      setHasMoreUsers(false);
      return;
    }

    if (!Array.isArray(items)) {
      setAllUsers([]);
      setHasMoreUsers(false);
      return;
    }

    if (items.length > 0) {
      if (userCurrentPage === 0) {
        setAllUsers(items);
      } else {
        setAllUsers((prev) => [...prev, ...items]);
      }

      const totalLoadedItems = (userCurrentPage + 1) * USER_PAGE_SIZE;
      const hasMore = totalLoadedItems < totalCount && items.length === USER_PAGE_SIZE;
      setHasMoreUsers(hasMore);
    } else {
      if (userCurrentPage === 0) {
        setAllUsers([]);
      }
      setHasMoreUsers(false);
    }
  }, [usersData, userCurrentPage]);

  const handleUserScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 50;

      if (isNearBottom && hasMoreUsers && !isUsersLoading && !isUsersFetching && allUsers.length > 0) {
        setUserCurrentPage((prev) => prev + 1);
      }
    },
    [hasMoreUsers, isUsersLoading, isUsersFetching, allUsers.length],
  );

  const handleUserWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const element = e.currentTarget;
    if (element) {
      element.scrollTop += e.deltaY;
      const scrollEvent = new Event("scroll", { bubbles: true });
      element.dispatchEvent(scrollEvent);
    }
  }, []);

  const handleUserSearchChange = useCallback((value: string) => {
    setUserSearchTerm(value);
    setUserCurrentPage(0);
    setAllUsers([]);
    setHasMoreUsers(true);
  }, []);

  useEffect(() => {
    if (!isUserPopoverOpen) {
      setUserSearchTerm("");
      setDebouncedUserSearch("");
      setUserCurrentPage(0);
    }
  }, [isUserPopoverOpen]);

  const handleFilterTypeChange = (type: "name" | "email") => {
    setQueryParams((prev: any) => ({
      ...prev,
      filter_type: type,
      user_id: "",
      user_email: "",
      page: 1,
    }));
    setUserSearchTerm("");
    setDebouncedUserSearch("");
    setUserCurrentPage(0);
    setAllUsers([]);
    setHasMoreUsers(true);
  };

  const handleUserSelection = (userId: string) => {
    setQueryParams((prev: any) => ({
      ...prev,
      user_id: userId,
      user_email: "",
      page: 1,
    }));
    setIsUserPopoverOpen(false);
  };

  const handleEmailSelection = (email: string, userId = "") => {
    // The unified conversations store filters by participant id, so keep the resolved
    // user_id alongside the email (email is kept only for the filter chip's display).
    setQueryParams((prev: any) => ({
      ...prev,
      user_email: email,
      user_id: userId,
      page: 1,
    }));
    setIsUserPopoverOpen(false);
  };

  const getSelectedLabel = (): string => {
    if (currentFilterType === "name" && queryParams.user_id) {
      const user = allUsers.find((u) => u.itemId === queryParams.user_id);
      if (user) return `${user.firstName} ${user.lastName}`;
      return "Selected user";
    }
    if (currentFilterType === "email" && queryParams.user_email) {
      return queryParams.user_email;
    }
    return currentFilterType === "name" ? "Select User" : "Select Email";
  };

  return {
    userSearchTerm,
    allUsers,
    isUsersLoading,
    isUsersFetching,
    isUserPopoverOpen,
    setIsUserPopoverOpen,
    commandListRef,
    handleUserScroll,
    handleUserWheel,
    handleUserSearchChange,
    handleFilterTypeChange,
    handleUserSelection,
    handleEmailSelection,
    getSelectedLabel,
  };
};
